'use client';

/* ──────────────────────────────────────────────
 *  useScrollToError — amène le message d'erreur d'une étape
 *  dans l'écran quand il apparaît. Partagé par les tunnels
 *  réservation, carte cadeau et course : l'action principale
 *  vit dans la barre collée en bas (`TunnelFooterBar`) et le
 *  message est rendu en fin de contenu — sur un téléphone il
 *  tombe sous le pli et le bouton semble ne rien faire (revue
 *  MI24). Le message porte `role="alert"` (annonce lecteur
 *  d'écran) ; `prefers-reduced-motion` respecté ; sans
 *  `scrollIntoView` (jsdom), rien ne bouge.
 * ────────────────────────────────────────────── */

import { type RefObject, useEffect, useRef } from 'react';

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * @param error message affiché (vide ou nul : aucun message) ; chaque nouvelle valeur non vide déclenche le défilement.
 * @returns ref à poser sur l'élément du message (`<p className="bt-error" role="alert" ref={ref}>`).
 */
export function useScrollToError<T extends HTMLElement = HTMLParagraphElement>(
  error: string | null | undefined,
): RefObject<T | null> {
  const ref = useRef<T | null>(null);

  useEffect((): void => {
    if (!error) return;
    const element = ref.current;
    if (!element || typeof element.scrollIntoView !== 'function') return;
    element.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }, [error]);

  return ref;
}
